import React from "react";
import { useRootStore } from "../../../../store/contexts";
import { ShowReference } from "../../../common/controls";
import { publisherDisplayId } from "../../../publisher/store/PublisherCommonStore";
import { ShowNumber } from "../../../common/controls";
import { ShowIntegerChoices } from "../../../common/controls";
import { useSnapshotModalStack } from "../../../common/controls";
import { EditionView as SnapshotEditionView } from "./EditionView";
import { EditionTypeView as SnapshotEditionTypeView } from "./EditionTypeView";
import { MusicalWorkView as SnapshotMusicalWorkView } from "./MusicalWorkView";
import { PhonogramView as SnapshotPhonogramView } from "./PhonogramView";
import { MusicalWorkLinkView as SnapshotMusicalWorkLinkView } from "./MusicalWorkLinkView";
import { PlatformView as SnapshotPlatformView } from "./PlatformView";
import { PlatformLinkTypeView as SnapshotPlatformLinkTypeView } from "./PlatformLinkTypeView";
import { MusicalGenreView as SnapshotMusicalGenreView } from "./MusicalGenreView";
import { InterpreterView as SnapshotInterpreterView } from "./InterpreterView";
import { TerritoryView as SnapshotTerritoryView } from "./TerritoryView";
import { WorkAuthorView as SnapshotWorkAuthorView } from "./WorkAuthorView";
import { AuthorComposerView as SnapshotAuthorComposerView } from "./AuthorComposerView";
import { CountryView as SnapshotCountryView } from "./CountryView";
import { ProvinceStateView as SnapshotProvinceStateView } from "./ProvinceStateView";
import { AuthorCollectingSocietyView as SnapshotAuthorCollectingSocietyView } from "./AuthorCollectingSocietyView";
import { CollectiveManagementSocietyView as SnapshotCollectiveManagementSocietyView } from "./CollectiveManagementSocietyView";
import { PublisherView as SnapshotPublisherView } from "./PublisherView";
import { PublisherRepresentativeView as SnapshotPublisherRepresentativeView } from "./PublisherRepresentativeView";
import { PublisherCollectingSocietyView as SnapshotPublisherCollectingSocietyView } from "./PublisherCollectingSocietyView";
import { EditionDocumentationView as SnapshotEditionDocumentationView } from "./EditionDocumentationView";
import { EditionContractPublisherView as SnapshotEditionContractPublisherView } from "./EditionContractPublisherView";
import { AssociatedDocumentEditionView as SnapshotAssociatedDocumentEditionView } from "./AssociatedDocumentEditionView";
import { EditionDocumentTypeView as SnapshotEditionDocumentTypeView } from "./EditionDocumentTypeView";
import { UserAttachmentEditionView as SnapshotUserAttachmentEditionView } from "./UserAttachmentEditionView";
import {
  useSnapshotExtension,
  useSnapshotExtensionTop,
} from "../../../common/controls";
import { FormControl } from "@mui/material";

type WorkAuthorPublisherViewProps = {
  snapshotData: any;
  // Propagated from the top-level snapshot view so nested tables can resolve
  // the snapshot-scoped file-download endpoint (/<snapshotEntityName>.data/
  // <snapshotRecordId>/<fileUuid>). Both are optional at the type level but
  // required at runtime for the download / view affordances to work.
  snapshotEntityName?: string;
  snapshotRecordId?: string;
};

export const WorkAuthorPublisherView: React.FC<
  WorkAuthorPublisherViewProps
> = ({ snapshotData, snapshotEntityName, snapshotRecordId }) => {
  const { langStore } = useRootStore();
  const renderExtensionTop = useSnapshotExtensionTop("WorkAuthorPublisher");
  const renderExtension = useSnapshotExtension("WorkAuthorPublisher");

  const { pushModal, modalElement } = useSnapshotModalStack(
    (entityName, data) => {
      switch (entityName) {
        case "Edition":
          return (
            <SnapshotEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionType":
          return (
            <SnapshotEditionTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWork":
          return (
            <SnapshotMusicalWorkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Phonogram":
          return (
            <SnapshotPhonogramView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalWorkLink":
          return (
            <SnapshotMusicalWorkLinkView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Platform":
          return (
            <SnapshotPlatformView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PlatformLinkType":
          return (
            <SnapshotPlatformLinkTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "MusicalGenre":
          return (
            <SnapshotMusicalGenreView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Interpreter":
          return (
            <SnapshotInterpreterView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Territory":
          return (
            <SnapshotTerritoryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "WorkAuthor":
          return (
            <SnapshotWorkAuthorView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorComposer":
          return (
            <SnapshotAuthorComposerView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Country":
          return (
            <SnapshotCountryView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "ProvinceState":
          return (
            <SnapshotProvinceStateView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AuthorCollectingSociety":
          return (
            <SnapshotAuthorCollectingSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "CollectiveManagementSociety":
          return (
            <SnapshotCollectiveManagementSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "Publisher":
          return (
            <SnapshotPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherRepresentative":
          return (
            <SnapshotPublisherRepresentativeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "PublisherCollectingSociety":
          return (
            <SnapshotPublisherCollectingSocietyView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionDocumentation":
          return (
            <SnapshotEditionDocumentationView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionContractPublisher":
          return (
            <SnapshotEditionContractPublisherView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "AssociatedDocumentEdition":
          return (
            <SnapshotAssociatedDocumentEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "EditionDocumentType":
          return (
            <SnapshotEditionDocumentTypeView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        case "UserAttachmentEdition":
          return (
            <SnapshotUserAttachmentEditionView
              snapshotData={data}
              snapshotEntityName={snapshotEntityName}
              snapshotRecordId={snapshotRecordId}
            />
          );
        default:
          return null;
      }
    }
  );
  return (
    <>
      {renderExtensionTop ? renderExtensionTop(snapshotData) : null}
      <FormControl fullWidth>
        <ShowReference
          label={langStore.WorkAuthorPublisherPublisherLabel}
          selectedValue={
            snapshotData?.publisher
              ? langStore.displayIdByLang(
                  publisherDisplayId(snapshotData?.publisher, langStore)
                )
              : ""
          }
          onView={() => pushModal("Publisher", snapshotData?.publisher)}
        />
        <ShowNumber
          label={langStore.WorkAuthorPublisherPercentageLabel}
          value={snapshotData?.percentage ?? null}
          decimalScale={2}
        />
        <ShowIntegerChoices
          label={langStore.WorkAuthorPublisherDurationInYearsLabel}
          value={snapshotData?.durationInYears ?? null}
          unitSingular={"año"}
          unitPlural={"años"}
          nullText={langStore.WorkAuthorPublisherDurationInYearsNullText}
        />
        <ShowIntegerChoices
          label={langStore.WorkAuthorPublisherAutoRenewalYearsLabel}
          value={snapshotData?.autoRenewalYears ?? null}
          unitSingular={"año"}
          unitPlural={"años"}
          nullText={langStore.WorkAuthorPublisherAutoRenewalYearsNullText}
        />
      </FormControl>
      {renderExtension ? renderExtension(snapshotData) : null}

      {modalElement}
    </>
  );
};
